import DOMPurify from 'dompurify'
import { ArrowRight, Code, Edit3, Hash, Link2, Plus, Send, Target, Trash2 } from 'lucide-react'
import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// Helper function to detect if text contains HTML tags
const containsHtml = (text: string | null | undefined): boolean => {
  if (!text) return false
  // Check for HTML tags like <a>, <ul>, <li>, <p>, etc.
  const htmlTagRegex = /<\/?[a-z][^>]*>/i
  return htmlTagRegex.test(text)
}

// Helper function to detect if text contains links (markdown, HTML, or URL patterns)
const containsLinks = (text: string | null | undefined): boolean => {
  if (!text) return false
  // Check for markdown links [text](url), HTML links <a href="url">, or plain URLs
  const markdownLinkRegex = /\[([^\]]+)\]\([^)]+\)/g
  const htmlLinkRegex = /<a\s+[^>]*href=["']([^"']+)["'][^>]*>/i
  const urlRegex = /https?:\/\/[^\s\]]+/g
  return markdownLinkRegex.test(text) || htmlLinkRegex.test(text) || urlRegex.test(text)
}

// Safe HTML sanitization for diff content
function sanitizeHtml(htmlContent: string): string {
  return DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'p',
      'br',
      'strong',
      'em',
      'u',
      'a',
      'ul',
      'ol',
      'li',
      'blockquote',
      'code',
      'div',
      'span',
    ],
    ALLOWED_ATTR: ['href', 'target', 'src', 'alt', 'title', 'class', 'style'],
    ALLOW_DATA_ATTR: false,
    // Transform links to be safe and properly styled
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  })
}

// Enhanced content renderer for diff text with mixed HTML/Markdown support
const DiffText = ({ children, className = '' }: { children: string; className?: string }) => {
  // Check if content contains HTML tags
  const hasHtml = containsHtml(children)

  if (hasHtml) {
    // Handle HTML content with proper sanitization
    const sanitizedHtml = sanitizeHtml(children)

    // Apply link styling to existing HTML links
    const styledHtml = sanitizedHtml.replace(
      /<a\s+([^>]*href=["']([^"']+)["'][^>]*)>([^<]+)<\/a>/gi,
      (match, attrs, href, text) => {
        // Ensure target="_blank" and proper styling
        const hasTarget = /target\s*=/i.test(attrs)
        const hasRel = /rel\s*=/i.test(attrs)
        const targetAttr = hasTarget ? '' : ' target="_blank"'
        const relAttr = hasRel ? '' : ' rel="noopener noreferrer"'

        return `<a ${attrs}${targetAttr}${relAttr} class="inline-flex items-center gap-1 font-medium text-blue-600 hover:text-blue-800 underline" style="color: #2563eb; font-weight: 500; z-index: 10; position: relative;">${text}<svg class="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>`
      }
    )

    return (
      <div
        className={`prose prose-sm max-w-none ${className}`}
        dangerouslySetInnerHTML={{ __html: styledHtml }}
      />
    )
  }

  // Handle pure markdown content
  return (
    <div className={`prose prose-sm max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <div className="mb-2 last:mb-0">{children}</div>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          ul: ({ children }) => (
            <ul className="my-2 list-inside list-disc space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="my-2 list-inside list-decimal space-y-1">{children}</ol>
          ),
          li: ({ children }) => <li>{children}</li>,
          code: ({ children }) => (
            <code className="rounded bg-gray-200/50 px-1 py-0.5 font-mono text-xs">{children}</code>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              className="!relative !z-10 !inline-flex !items-center !gap-1 !font-medium !text-blue-600 !underline !no-underline hover:!text-blue-800 hover:!underline"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                window.open(href, '_blank', 'noopener,noreferrer')
              }}
              style={{
                textDecoration: 'none',
                color: '#2563eb',
                fontWeight: '500',
                position: 'relative',
                zIndex: 10,
              }}
            >
              {children}
              <Link2 className="ml-1 h-3 w-3" />
            </a>
          ),
          // Handle inline content better
          h1: ({ children }) => <h1 className="mb-2 text-lg font-bold">{children}</h1>,
          h2: ({ children }) => <h2 className="mb-2 text-base font-semibold">{children}</h2>,
          h3: ({ children }) => <h3 className="mb-1 text-sm font-semibold">{children}</h3>,
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l-2 border-gray-300 pl-2 italic">
              {children}
            </blockquote>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}

// Helper utilities for cleaner message formatting
function truncateText(text: string, maxLength: number = 150): string {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength).trim() + '...'
}

function formatChangeTypeLabel(changeType: string): string {
  const labels = {
    replace: 'Replace content',
    insert_after: 'Add content after',
    insert_before: 'Add content before',
    delete: 'Remove content',
  }
  return labels[changeType as keyof typeof labels] || 'Modify content'
}

function formatKeywordsList(keywords: string[]): string {
  return keywords.join(', ')
}

// Message formatting utilities
function formatContentChangeMessage(change: ContentChange, articleUrl: string): string {
  const { changeType, findText, replaceWith, changeReason, keywordsTargeted } = change

  const actionLabel = formatChangeTypeLabel(changeType)
  const findTextFormatted = findText ? truncateText(findText) : ''
  const replaceWithFormatted = replaceWith ? truncateText(replaceWith) : ''

  const changeDetails = [`• **Action**: ${actionLabel}`]

  if (findTextFormatted) {
    changeDetails.push(`• **Find**: "${findTextFormatted}"`)
  }

  if (
    replaceWithFormatted &&
    (changeType === 'replace' || changeType === 'insert_after' || changeType === 'insert_before')
  ) {
    const label = changeType === 'replace' ? 'Replace with' : 'Content to add'
    changeDetails.push(`• **${label}**: "${replaceWithFormatted}"`)
  }

  if (changeReason) {
    changeDetails.push(`• **Reason**: ${changeReason}`)
  }

  if (keywordsTargeted && keywordsTargeted.length > 0) {
    changeDetails.push(`• **Target Keywords**: ${formatKeywordsList(keywordsTargeted)}`)
  }

  // Generate explicit change object for isolated content change
  const changeObject = {
    changeType,
    findText: findText || '',
    replaceWith: replaceWith || '',
    changeReason: changeReason || `${actionLabel} for SEO optimization`,
  }

  return `I want to publish this content change to ${articleUrl}:

${changeDetails.join('\n')}

Use shopifyWebPublisher with:
- action: 'update'
- targetSlug: extract from URL
- changes: [${JSON.stringify(changeObject)}]

Apply only this specific content change, not other changes.`
}

function formatSeoChangeMessage(
  type: 'metaTitle' | 'metaDescription',
  update: SeoUpdate,
  articleUrl: string
): string {
  const fieldLabel = type === 'metaTitle' ? 'Meta Title' : 'Meta Description'

  const changeDetails = []

  if (update.newValue) {
    changeDetails.push(`• **New ${fieldLabel}**: "${update.newValue}"`)
  }

  if (update.oldValue) {
    changeDetails.push(`• **Previous**: "${truncateText(update.oldValue)}"`)
  }

  changeDetails.push(`• **Purpose**: Improved SEO performance`)

  // Generate explicit metadata object for isolated change
  const metaUpdates: any = {}
  if (type === 'metaTitle' && update.newValue) {
    metaUpdates.title = update.newValue
    metaUpdates.metaTitle = update.newValue
  } else if (type === 'metaDescription' && update.newValue) {
    metaUpdates.summary = update.newValue
    metaUpdates.metaDescription = update.newValue
  }

  return `I want to publish this SEO ${fieldLabel.toLowerCase()} change to ${articleUrl}:

${changeDetails.join('\n')}

Use shopifyWebPublisher with:
- action: 'update'
- targetSlug: extract from URL
- metaUpdates: ${JSON.stringify(metaUpdates)}

Apply only this specific ${fieldLabel.toLowerCase()} change, not other changes.`
}

function formatKeywordsChangeMessage(
  update: {
    added?: string[] | null
    removed?: string[] | null
  },
  articleUrl: string
): string {
  const keywordsToAdd = update.added && update.added.length > 0 ? update.added : []
  const keywordsToRemove = update.removed && update.removed.length > 0 ? update.removed : []

  const changeDetails = []

  if (keywordsToAdd.length > 0) {
    changeDetails.push(`• **Adding Keywords**: ${formatKeywordsList(keywordsToAdd)}`)
  }

  if (keywordsToRemove.length > 0) {
    changeDetails.push(`• **Removing Keywords**: ${formatKeywordsList(keywordsToRemove)}`)
  }

  changeDetails.push(`• **Purpose**: Improved SEO targeting`)

  // Generate explicit metadata object for isolated keyword change
  const metaUpdates = {
    keywords: keywordsToAdd, // Use only the added keywords for isolated change
  }

  return `I want to publish this SEO keyword change to ${articleUrl}:

${changeDetails.join('\n')}

Use shopifyWebPublisher with:
- action: 'update'
- targetSlug: extract from URL
- metaUpdates: ${JSON.stringify(metaUpdates)}

Apply only these keyword changes, not other changes.`
}

interface ContentChange {
  changeType: 'replace' | 'insert_after' | 'insert_before' | 'delete'
  findText?: string | null
  replaceWith?: string | null
  contextBefore?: string | null
  contextAfter?: string | null
  changeReason?: string | null
  keywordsTargeted?: string[] | null
}

interface SeoUpdate {
  status: 'modified' | 'unchanged'
  oldValue?: string | null
  newValue?: string | null
}

interface SeoUpdates {
  metaTitle?: SeoUpdate | null
  metaDescription?: SeoUpdate | null
  keywords?: {
    status: 'modified' | 'unchanged'
    added?: string[] | null
    removed?: string[] | null
  } | null
  jsonLd?: {
    status: 'created' | 'modified' | 'unchanged'
    oldValue?: any
    newValue?: any
  } | null
}

interface DiffVisualizationProps {
  changes: ContentChange[]
  seoUpdates: SeoUpdates
  onPublishChange?: (changeDescription: string) => void
  articleUrl: string
}

const ChangeTypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'replace':
      return <Edit3 className="h-4 w-4 text-blue-600" />
    case 'insert_after':
    case 'insert_before':
      return <Plus className="h-4 w-4 text-green-600" />
    case 'delete':
      return <Trash2 className="h-4 w-4 text-red-600" />
    default:
      return <Edit3 className="h-4 w-4 text-gray-600" />
  }
}

const ChangeTypeBadge = ({ type }: { type: string }) => {
  const config = {
    replace: {
      label: 'Replace',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
    },
    insert_after: {
      label: 'Insert After',
      bg: 'bg-green-50',
      text: 'text-green-700',
      border: 'border-green-200',
    },
    insert_before: {
      label: 'Insert Before',
      bg: 'bg-green-50',
      text: 'text-green-700',
      border: 'border-green-200',
    },
    delete: { label: 'Delete', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  }

  const style = config[type as keyof typeof config] || config.replace

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${style.bg} ${style.text} ${style.border}`}
    >
      <ChangeTypeIcon type={type} />
      {style.label}
    </span>
  )
}

const KeywordBadge = ({ keyword }: { keyword: string }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700">
    <Target className="h-3 w-3" />
    {keyword}
  </span>
)

const TextDiff = ({ change }: { change: ContentChange }) => {
  const { changeType, findText, replaceWith, contextBefore, contextAfter } = change

  if (changeType === 'delete') {
    return (
      <div className="space-y-2">
        {contextBefore && (
          <div className="text-sm italic text-gray-600">...{contextBefore.slice(-50)}</div>
        )}
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <div className="mb-1 text-sm font-medium text-red-800">Deleted:</div>
          <div className="text-sm text-red-800 [&:not(a)]:line-through">
            <DiffText className="prose-red [&_a:hover]:text-red-800 [&_a]:font-medium [&_a]:text-red-600 [&_a]:no-underline [&_p]:text-red-800">
              {findText || ''}
            </DiffText>
          </div>
        </div>
        {contextAfter && (
          <div className="text-sm italic text-gray-600">{contextAfter.slice(0, 50)}...</div>
        )}
      </div>
    )
  }

  if (changeType === 'insert_after' || changeType === 'insert_before') {
    return (
      <div className="space-y-2">
        {contextBefore && (
          <div className="text-sm italic text-gray-600">...{contextBefore.slice(-50)}</div>
        )}
        <div className="space-y-2">
          {findText && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mb-1 text-sm font-medium text-gray-700">
                {changeType === 'insert_after' ? 'After this text:' : 'Before this text:'}
              </div>
              <DiffText className="text-gray-800 [&_a:hover]:text-blue-800 [&_a]:text-blue-600 [&_p]:text-gray-800">
                {findText || ''}
              </DiffText>
            </div>
          )}
          <div className="rounded-lg border border-green-200 bg-green-50 p-3">
            <div className="mb-1 text-sm font-medium text-green-800">Added:</div>
            <DiffText className="prose-green [&_a:hover]:text-blue-800 [&_a]:font-medium [&_a]:text-blue-600 [&_p]:text-green-800">
              {replaceWith || ''}
            </DiffText>
          </div>
        </div>
        {contextAfter && (
          <div className="text-sm italic text-gray-600">{contextAfter.slice(0, 50)}...</div>
        )}
      </div>
    )
  }

  // Replace type
  return (
    <div className="space-y-2">
      {contextBefore && (
        <div className="text-sm italic text-gray-600">...{contextBefore.slice(-50)}</div>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <div className="mb-1 text-sm font-medium text-red-800">Before:</div>
          <div className="text-sm text-red-800 [&:not(a)]:line-through">
            <DiffText className="prose-red [&_a:hover]:text-red-800 [&_a]:font-medium [&_a]:text-red-600 [&_a]:no-underline [&_p]:text-red-800">
              {findText || ''}
            </DiffText>
          </div>
        </div>
        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <div className="mb-1 text-sm font-medium text-green-800">After:</div>
          <DiffText className="prose-green [&_a:hover]:text-blue-800 [&_a]:font-medium [&_a]:text-blue-600 [&_p]:text-green-800">
            {replaceWith || ''}
          </DiffText>
        </div>
      </div>
      <div className="flex justify-center md:hidden">
        <ArrowRight className="h-4 w-4 text-gray-400" />
      </div>
      {contextAfter && (
        <div className="text-sm italic text-gray-600">{contextAfter.slice(0, 50)}...</div>
      )}
    </div>
  )
}

const ContentChangeCard = ({
  change,
  index,
  onPublish,
  articleUrl,
}: {
  change: ContentChange
  index: number
  onPublish?: (description: string) => void
  articleUrl: string
}) => {
  const hasLinks = containsLinks(change.findText) || containsLinks(change.replaceWith)
  const hasHtmlContent = containsHtml(change.findText) || containsHtml(change.replaceWith)

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-500">Change {index + 1}</span>
          <ChangeTypeBadge type={change.changeType} />
          {hasLinks && (
            <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
              <Link2 className="h-3 w-3" />
              Contains Links
            </span>
          )}
          {hasHtmlContent && (
            <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700">
              <Code className="h-3 w-3" />
              Contains HTML
            </span>
          )}
        </div>
        {onPublish && (
          <button
            onClick={() => onPublish(formatContentChangeMessage(change, articleUrl))}
            className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <Send className="h-3 w-3" />
            Publish this
          </button>
        )}
      </div>

      <TextDiff change={change} />

      {change.changeReason && (
        <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 p-3">
          <div className="text-sm text-blue-800">
            <span className="font-medium">Reason:</span>{' '}
            <DiffText className="prose-blue inline">{change.changeReason}</DiffText>
          </div>
        </div>
      )}

      {change.keywordsTargeted && change.keywordsTargeted.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-xs text-gray-600">Targeting keywords:</div>
          <div className="flex flex-wrap gap-1">
            {change.keywordsTargeted.map((keyword, i) => (
              <KeywordBadge key={i} keyword={keyword} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const SeoUpdateCard = ({
  title,
  update,
  onPublish,
  articleUrl,
}: {
  title: string
  update: SeoUpdate
  onPublish?: (description: string) => void
  articleUrl: string
}) => {
  if (update.status !== 'modified') return null

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Hash className="h-4 w-4 text-indigo-600" />
          <span className="font-medium text-gray-900">{title}</span>
          <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
            SEO Update
          </span>
        </div>
        {onPublish && (
          <button
            onClick={() =>
              onPublish(
                formatSeoChangeMessage(
                  title === 'Meta Title' ? 'metaTitle' : 'metaDescription',
                  update,
                  articleUrl
                )
              )
            }
            className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <Send className="h-3 w-3" />
            Publish this
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {update.oldValue && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="mb-1 text-sm font-medium text-red-800">Before:</div>
            <div className="text-sm text-red-800 line-through">
              <DiffText className="prose-red">{update.oldValue}</DiffText>
            </div>
          </div>
        )}
        {update.newValue && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3">
            <div className="mb-1 text-sm font-medium text-green-800">After:</div>
            <div className="rounded bg-green-100 px-1 text-sm text-green-800">
              <DiffText className="prose-green">{update.newValue}</DiffText>
            </div>
          </div>
        )}
      </div>
      <div className="mt-2 flex justify-center md:hidden">
        <ArrowRight className="h-4 w-4 text-gray-400" />
      </div>
    </div>
  )
}

const KeywordsUpdateCard = ({
  update,
  onPublish,
  articleUrl,
}: {
  update: NonNullable<SeoUpdates['keywords']>
  onPublish?: (description: string) => void
  articleUrl: string
}) => {
  if (update.status !== 'modified' || (!update.added?.length && !update.removed?.length))
    return null

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Hash className="h-4 w-4 text-indigo-600" />
          <span className="font-medium text-gray-900">Keywords</span>
          <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
            SEO Update
          </span>
        </div>
        {onPublish && (
          <button
            onClick={() => onPublish(formatKeywordsChangeMessage(update, articleUrl))}
            className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <Send className="h-3 w-3" />
            Publish this
          </button>
        )}
      </div>

      <div className="space-y-3">
        {update.removed && update.removed.length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="mb-2 text-sm font-medium text-red-800">Removed Keywords:</div>
            <div className="flex flex-wrap gap-1">
              {update.removed.map((keyword, i) => (
                <span
                  key={i}
                  className="rounded-full border border-red-300 bg-red-100 px-2 py-1 text-xs font-medium text-red-800 line-through"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}

        {update.added && update.added.length > 0 && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3">
            <div className="mb-2 text-sm font-medium text-green-800">Added Keywords:</div>
            <div className="flex flex-wrap gap-1">
              {update.added.map((keyword, i) => (
                <span
                  key={i}
                  className="rounded-full border border-green-300 bg-green-100 px-2 py-1 text-xs font-medium text-green-800"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function JsonLdUpdateCard({
  update,
  onPublish,
  articleUrl,
}: {
  update: any
  onPublish?: (message: string) => void
  articleUrl: string
}) {
  const [isExpanded, setIsExpanded] = React.useState(false)

  const getStatusBadge = () => {
    const statusConfig = {
      created: { label: 'Created', color: 'bg-green-100 text-green-800 border-green-200' },
      modified: { label: 'Modified', color: 'bg-blue-100 text-blue-800 border-blue-200' },
      unchanged: { label: 'Unchanged', color: 'bg-gray-100 text-gray-800 border-gray-200' },
    }

    const config =
      statusConfig[update.status as keyof typeof statusConfig] || statusConfig.unchanged
    return (
      <span className={`rounded-full border px-2 py-1 text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const formatJsonLdChangeMessage = (update: any, articleUrl: string): string => {
    const metaUpdates = update.newValue ? { jsonLd: update.newValue } : {}
    return `Update JSON-LD structured data for ${articleUrl}:

Changes made: ${update.changes?.join(', ') || 'Updated JSON-LD structure'}

Use shopifyWebPublisher with:
- action: 'update'
- targetSlug: extract from URL
- metaUpdates: ${JSON.stringify(metaUpdates)}

Apply only this JSON-LD change, not other changes.`
  }

  if (update.status === 'unchanged') {
    return null
  }

  return (
    <div className="rounded-xl border border-purple-200 bg-purple-50 p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-purple-100 p-2">
            <Code className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-purple-900">JSON-LD Structured Data</h3>
            <p className="text-sm text-purple-700">Schema.org BlogPosting structured data</p>
          </div>
          {getStatusBadge()}
          <span className="rounded-full border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700">
            SEO Update
          </span>
        </div>
        {onPublish && (
          <button
            onClick={() => onPublish(formatJsonLdChangeMessage(update, articleUrl))}
            className="inline-flex items-center gap-1 rounded-md border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
          >
            <Send className="h-3 w-3" />
            Publish this
          </button>
        )}
      </div>

      <div className="space-y-3">
        {update.changes && update.changes.length > 0 && (
          <div className="bg-purple-25 rounded-lg border border-purple-200 p-3">
            <div className="mb-2 text-sm font-medium text-purple-800">Changes Made:</div>
            <div className="flex flex-wrap gap-1">
              {update.changes.map((change: string, i: number) => (
                <span
                  key={i}
                  className="rounded-full border border-purple-300 bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800"
                >
                  {change}
                </span>
              ))}
            </div>
          </div>
        )}

        {(update.oldValue || update.newValue) && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-purple-800">JSON-LD Data</span>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-xs text-purple-600 hover:text-purple-800"
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </button>
            </div>

            {isExpanded && (
              <div className="grid gap-3 md:grid-cols-2">
                {update.oldValue && (
                  <div>
                    <div className="mb-1 text-xs font-medium text-red-600">Previous:</div>
                    <pre className="max-h-48 overflow-auto rounded border bg-red-50 p-2 text-xs text-red-800">
                      {JSON.stringify(update.oldValue, null, 2)}
                    </pre>
                  </div>
                )}
                {update.newValue && (
                  <div>
                    <div className="mb-1 text-xs font-medium text-green-600">Updated:</div>
                    <pre className="max-h-48 overflow-auto rounded border bg-green-50 p-2 text-xs text-green-800">
                      {JSON.stringify(update.newValue, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export function DiffVisualization({
  changes,
  seoUpdates,
  onPublishChange,
  articleUrl,
}: DiffVisualizationProps) {
  const hasContentChanges = changes && changes.length > 0
  const hasSeoUpdates =
    seoUpdates &&
    (seoUpdates.metaTitle?.status === 'modified' ||
      seoUpdates.metaDescription?.status === 'modified' ||
      seoUpdates.keywords?.status === 'modified' ||
      (seoUpdates.jsonLd && seoUpdates.jsonLd.status !== 'unchanged'))

  if (!hasContentChanges && !hasSeoUpdates) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center">
        <div className="mb-4 text-4xl">📝</div>
        <div className="mb-2 text-lg font-medium text-gray-600">No Changes to Display</div>
        <div className="text-sm text-gray-500">
          No content or SEO changes were made to this article.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* SEO Updates */}
      {seoUpdates?.metaTitle && (
        <SeoUpdateCard
          title="Meta Title"
          update={seoUpdates.metaTitle}
          onPublish={onPublishChange}
          articleUrl={articleUrl}
        />
      )}

      {seoUpdates?.metaDescription && (
        <SeoUpdateCard
          title="Meta Description"
          update={seoUpdates.metaDescription}
          onPublish={onPublishChange}
          articleUrl={articleUrl}
        />
      )}

      {seoUpdates?.keywords && (
        <KeywordsUpdateCard
          update={seoUpdates.keywords}
          onPublish={onPublishChange}
          articleUrl={articleUrl}
        />
      )}

      {seoUpdates?.jsonLd && (
        <JsonLdUpdateCard
          update={seoUpdates.jsonLd}
          onPublish={onPublishChange}
          articleUrl={articleUrl}
        />
      )}

      {/* Content Changes */}
      {hasContentChanges && (
        <div className="space-y-4">
          {changes.map((change, index) => (
            <ContentChangeCard
              key={index}
              change={change}
              index={index}
              onPublish={onPublishChange}
              articleUrl={articleUrl}
            />
          ))}
        </div>
      )}
    </div>
  )
}
